import React, { useState, useReducer, useCallback } from 'react';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import DashboardLayout from '../../layouts/Dashboard';
import { AtCircularProgress } from '../../components/AtCircularProgress';
import { PanelsLayout } from '../DynamicPanel/components/PanelsLayout';
import { Alert, AlertTitle } from '@material-ui/lab';
import { Box, Button, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { FixedPanels } from './components/FixedPanels';
import { HomeFilters } from './components/HomeFilters';
import { useDynamicPanelHomeUser } from '../DynamicPanel/hooks/useDynamicPanelsUser';
import { useObjectivesList } from '../../hooks/useObjectivesList';
import { DynamicViewDispatchContext, dynamicViewReducer, DynamicViewStateContext, initialState } from '../DynamicPanel/context/dynamic-view-context';
import { PanelsSection } from '../DynamicPanel/components/layouts-management/PanelsSection';

const styles = theme => ({
  root: {
    padding: theme.spacing(2),
    backgroundColor: 'inherit',
    height: '100%',
  },
  welcome: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(3),
    color: '#111827',
  },
  errorContainer: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2)
  }
});

const ErrorDashboard = ({ classes, onRetry, retryLabel, errorMessage }) => (
  <Box className={classes.errorContainer}>
    <Alert severity="error">
      <AlertTitle>Error</AlertTitle>
      {errorMessage || 'Hubo un error al cargar tus dashboards.'}
    </Alert>
    <Button variant="contained" color="primary" onClick={onRetry}>
      {retryLabel || 'Reintentar'}
    </Button>
  </Box>
);

const ButtonChangePanel = ({ onClick, homePanel, label }) => (
  <Box
    onClick={onClick}
    style={{
      width: '100%',
      padding: '16px 20px',
      borderRadius: '16px',
      backgroundColor: '#F0E7FF',
      border: '2px dashed #C084FC',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      transition: 'all 0.2s ease-in-out',
    }}
  >
    <Box
      style={{
        backgroundColor: '#E9E4FF',
        borderRadius: '50%',
        width: '42px',
        height: '42px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#6D28D9',
        flexShrink: 0,
      }}
    >
      <AddIcon />
    </Box>

    <Typography
      variant="subtitle1"
      style={{
        color: '#37248F',
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const Home = ({ classes, history }) => {
  const { t } = useTranslation();
  const [objectivesList, setObjectivesList] = useState({});
  const [state, dispatch] = useReducer(dynamicViewReducer, { ...initialState, refreshTime: 600000 });
  const [openPanels, setOpenPanels] = useState(false);
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);

  const { data: allMonitors, isLoading: isLoadingMonitors } = useObjectivesList({ fromStorage: true });

  const hasNoObjectives = !isLoadingMonitors && (!allMonitors || allMonitors.length === 0);

  React.useEffect(() => {
    if (allMonitors && allMonitors.length > 0 && !selectedObjectiveId) {
      setSelectedObjectiveId(allMonitors[0].objetivo_id);
    }
  }, [allMonitors, selectedObjectiveId]);

  const selectedObjective = React.useMemo(() => {
    if (!allMonitors || allMonitors.length === 0) return null;
    return allMonitors.find(obj => String(obj.objetivo_id) === String(selectedObjectiveId));
  }, [allMonitors, selectedObjectiveId]);

  const userName = sessionStorage.getItem('user_name') || 'Usuario';

  const handleCallback = useCallback((data) => {
    setObjectivesList(data);
  }, []);

  const { data: homePanel, isLoading, isError, refetch } = useDynamicPanelHomeUser({
      enabled: true,
      refetchInterval: 600000,
    });

  const synchronizedPanel = React.useMemo(() => {
    if (!homePanel) return null;
    if (!selectedObjectiveId) return homePanel;

    return {
      ...homePanel,
      objetivo_id: selectedObjectiveId,
      estructura_panel_dinamico: (homePanel.estructura_panel_dinamico || []).map(slot => ({
        ...slot,
        objetivoId: selectedObjectiveId
      }))
    };
  }, [homePanel, selectedObjectiveId]);

  const handleOpenPanels = useCallback(() => setOpenPanels(true), []);
  const handleClosePanels = useCallback(() => setOpenPanels(false), []);

  const changePanelLabel = homePanel && homePanel.panel_dinamico_usuario_id
    ? t('newSlotCard.changePanel')
    : t('newSlotCard.addNewCard');

  return (
    <DynamicViewStateContext.Provider value={state}>
      <DynamicViewDispatchContext.Provider value={dispatch}>
        <DashboardLayout title="Home" isFirstTime={true} callbackFromParent={handleCallback}>
          <div className={classes.root}>
            <Box  ml={1} display='flex' justifyContent='space-between' alignItems='center'>
              <Typography variant="h4" style={{ fontWeight: 800, color: '#1E293B', fontSize: '1.75rem' }}>
                {t('home.welcomeBack', { userName })}
              </Typography>
              {!hasNoObjectives && (
                <HomeFilters 
                  selectedObjectiveId={selectedObjectiveId} 
                  setSelectedObjectiveId={setSelectedObjectiveId} 
                />
              )}
            </Box>
            
            {hasNoObjectives ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                style={{
                  minHeight: 'calc(100vh - 250px)',
                  textAlign: 'center',
                  padding: '0 24px',
                }}
              >
                <Box
                  style={{
                    width: '88px',
                    height: '88px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '28px',
                    fontSize: '40px',
                  }}
                >
                  📊
                </Box>
                <Typography
                  variant="h4"
                  style={{
                    fontWeight: 700,
                    color: '#1E293B',
                    marginBottom: '16px',
                    fontSize: '1.5rem',
                  }}
                >
                  {t('home.noObjectives.title')}
                </Typography>
                <Typography
                  variant="body1"
                  style={{
                    color: '#64748B',
                    lineHeight: 1.7,
                    maxWidth: '480px',
                    fontSize: '1rem',
                  }}
                >
                  {t('home.noObjectives.description')}
                </Typography>
              </Box>
            ) : (
              <>
                <FixedPanels monitorsData={objectivesList} objective={selectedObjective} />

                {isLoading ? (
                  <Box display="flex" justifyContent="center" p={4}>
                    <AtCircularProgress />
                  </Box>
                ) : isError ? (
                  <ErrorDashboard
                    classes={classes}
                    onRetry={refetch}
                    retryLabel={t('text.update')}
                    errorMessage={t('dynamicPanel.panelsSection.loadError')}
                  />
                ) : (
                   synchronizedPanel && synchronizedPanel.panel_dinamico_usuario_id && <PanelsLayout initialPanel={synchronizedPanel} hideControls={true} />
                )}

                <Box mt={4}>
                  <ButtonChangePanel
                    onClick={handleOpenPanels}
                    homePanel={homePanel}
                    label={changePanelLabel}
                  />
                </Box>
                
                <PanelsSection isPanelsOpen={openPanels} handleClosePanels={handleClosePanels} />
              </>
            )}
          </div>
        </DashboardLayout>
      </DynamicViewDispatchContext.Provider>
    </DynamicViewStateContext.Provider>
  );
};

export default withRouter(withStyles(styles)(Home));